import { NotificationManager } from 'react-notifications'; // https://www.npmjs.com/package/react-notifications
import {
  ajaxDelete,
  fetchAbsolutePath,
  fetchJson,
  get,
  post,
  postJson,
  userAction,
} from '@/util/helpers/ajax-helpers';
import { createCustomEvent } from '@/util/createCustomEvent';
import { selectDashboardIdToAppliedFiltersRefreshIdMap } from '@/features/dashboards/dashboardsSlice';
import { TEXTBOX } from '@/components/ChartHolder.constants';
import { loading, completed, finished, standardErrorHandler } from './helpers';
import {
  DASHBOARDS_LOADED,
  DASHBOARD_UPDATED,
  DASHBOARD_LOADED,
  DASHBOARD_REFRESHED,
  DASHBOARD_CONTENT_LOADED,
  DASHBOARD_FILTERS_LOADED,
  DASHBOARD_CREATED,
  DASHBOARD_DELETED,
  WIDGET_ADDED,
  MODAL_SAVE_CLICKED,
  MODAL_DATA_LOADED,
  PIN_REFRESHED,
} from './constants';
import DashboardRef from '../util/DashboardRef';
import ModalShareAsHtml from '../components/modals/ModalShareAsHtml';
import DashboardFilterRef from '../util/DashboardFilterRef';
import LayerWhere from '../components/layers/LayerWhere';
import DashboardChartWidget from '../util/DashboardChartWidget';
import DashboardTextBoxWidget from '../util/DashboardTextboxWidget';

export function sharePublicDashboard(dashboard, publicId, publicPin) {
  return (dispatch) => {
    dispatch(loading(MODAL_DATA_LOADED));
    dispatch(
      completed(MODAL_DATA_LOADED, {
        modal: ModalShareAsHtml,
        publicId,
        publicPin,
        dashboard: true,
      }),
    );
  };
}

export function saveDashboard(state) {
  return (dispatch) => {
    dispatch(loading(DASHBOARD_UPDATED));
    const widgets = [
      ...state.chartWidgets.map(DashboardChartWidget.toJson),
      ...state.textWidgets.map(DashboardTextBoxWidget.toJson),
    ];
    postJson(`/dashboards/${state.active.id()}`, {
      _widgets: widgets,
    })
      .then((json) => {
        if (json.success) {
          dispatch(completed(DASHBOARD_UPDATED, json));
        } else {
          window.alert(
            'Problem saving dashboard.  Please contact us if the problem persists.',
          );
        }
      })
      .catch(standardErrorHandler(DASHBOARD_UPDATED, dispatch));
  };
}

export function deleteDashboard(dashboard) {
  return (dispatch) => {
    dispatch(loading(DASHBOARD_DELETED));
    ajaxDelete(`/dashboards/${dashboard.id()}`)
      .then((json) => {
        dispatch(completed(DASHBOARD_DELETED, { deleted: dashboard, json }));
        userAction('delete-dashboard', { dashboard_id: dashboard.id() });
      })
      .catch(standardErrorHandler(DASHBOARD_DELETED, dispatch));
  };
}

export function addDashboard(state) {
  return (dispatch) => {
    dispatch(completed(MODAL_SAVE_CLICKED, {})); // closes modal
    dispatch(loading(DASHBOARD_CREATED));
    postJson('/dashboards', {
      _time: state.time,
      _name: state.name.trim(),
      _existing: state.existing,
      _refreshFrequency: state.schedule,
    })
      .then((json) => {
        dispatch(
          completed(DASHBOARD_CREATED, {
            json,
            existing: state.existing,
          }),
        );
        const newDashboard = DashboardRef.build(json);
        if (!state.existing) {
          // now open the new dashboard
          dispatch(loadDashboard(newDashboard));
        }
        const action = !state.existing
          ? 'create-new-dashboard'
          : 'edit-dashboard';
        const meta = {
          dashboard_id: newDashboard.id(),
          name: newDashboard.name(),
          isPublic: newDashboard.isPublic(),
          refreshFrequency: newDashboard.refreshFrequency(),
          time: newDashboard.time(),
        };
        userAction(action, meta);
        window.dispatchEvent(
          createCustomEvent('trevor:DASHBOARD_CREATED', newDashboard.id()),
        );
      })
      .catch(standardErrorHandler(DASHBOARD_CREATED, dispatch));
  };
}

const loadedFiltersCallback = (
  json,
  dashboard,
  dispatch,
  dashboardRefreshId,
) => {
  const filters = json.map((f) => DashboardFilterRef.buildFromServer(f));
  if (dashboardRefreshId) {
    get(
      'loading filters applied values',
      `/dashboards/${dashboard.id()}/filtersValues?refreshId=${dashboardRefreshId}`,
    ).then((values) => {
      const appliedValues = values.map((v) => LayerWhere.buildFromServer(v));
      dispatch(
        completed(DASHBOARD_FILTERS_LOADED, {
          filters,
          appliedFilters: appliedValues,
        }),
      );
    });
  } else {
    dispatch(
      completed(DASHBOARD_FILTERS_LOADED, {
        filters,
        appliedFilters: [],
      }),
    );
  }
};

export const addTextBox = () => (dispatch) => {
  dispatch(completed(WIDGET_ADDED, { view: null, display: TEXTBOX }));
};

export function reloadDashboardFilters() {
  return (dispatch, getState) => {
    const { refreshId, active } = getState().dashboards;
    const dashboardIdToAppliedFiltersRefreshIdMap =
      selectDashboardIdToAppliedFiltersRefreshIdMap(getState());

    const refreshIdParam =
      dashboardIdToAppliedFiltersRefreshIdMap[active._id] ?? refreshId;

    dispatch(loading(DASHBOARD_FILTERS_LOADED));

    get('loading dashboard filters', `/dashboards/${active.id()}/filters`).then(
      (json) => loadedFiltersCallback(json, active, dispatch, refreshIdParam),
    );
  };
}

export function loadDashboards() {
  return (dispatch) => {
    dispatch(loading(DASHBOARDS_LOADED));
    fetchJson('/dashboards')
      .then((json) => {
        dispatch(completed(DASHBOARDS_LOADED, json));
      })
      .catch(standardErrorHandler(DASHBOARDS_LOADED, dispatch));
  };
}

const NULL_REFRESH_ID_VALUES = [null, 'null'];
const getDashboardRefreshPath = (dashboard) => {
  const hasRefreshId = !NULL_REFRESH_ID_VALUES.includes(dashboard.refreshId());
  return hasRefreshId ? `/refresh/${dashboard.refreshId()}` : '';
};

export function loadDashboard(dashboard) {
  return (dispatch) => {
    // 1. first load the widgets themselves
    dispatch(loading(DASHBOARD_LOADED, { dashboard }));
    fetchJson(
      `/dashboards/${dashboard.id()}${getDashboardRefreshPath(dashboard)}`,
    )
      .then((json) => {
        const dashboardRef = DashboardRef.build({
          ...json,
          _refreshId: dashboard.refreshId(),
        });
        dispatch(
          completed(DASHBOARD_LOADED, { json, dashboard: dashboardRef }),
        );
        dispatch(refreshWidgets());
        userAction('open-dashboard', { dashboard_id: dashboard.id() });
      })
      .catch(standardErrorHandler(DASHBOARD_LOADED, dispatch));
  };
}

export function loadPublicDashboard(dashboardPublicId, pin) {
  return (dispatch) => {
    // 1. first load the widgets themselves
    dispatch(loading(DASHBOARD_LOADED, {}));
    fetchAbsolutePath(
      `/share/dashboards/${dashboardPublicId}/data?pin=${pin}`,
      true,
    )
      .then((json) => {
        const dashboardRef = new DashboardRef(null, null, json._name, null);
        // 1. render widgets
        dispatch(
          completed(DASHBOARD_LOADED, { json, dashboard: dashboardRef }),
        );
        // 2. fill them with data
        dispatch(completed(DASHBOARD_CONTENT_LOADED, { json }));
        userAction('open-public-dashboard', {
          dashboard_public_id: dashboardPublicId,
        });
      })
      .catch(standardErrorHandler(DASHBOARD_LOADED, dispatch));
  };
}

let latestDashboardRefresh = 1;

export function refreshDashboard(forceRefresh, filtersAfter, e) {
  return (dispatch, getState) => {
    const { active, views, appliedFilters } = getState().dashboards;

    // 1. first kick off the refresh
    const userClickedRefreshButton = !!e;
    dispatch(loading(DASHBOARD_REFRESHED));
    const currentRefresh = ++latestDashboardRefresh;
    post('refreshing dashboard', `/dashboards/${active.id()}/refresh`, {
      additionalViews:
        appliedFilters && !userClickedRefreshButton
          ? null // user changed filters, so only refresh views linked to filter
          : views.filter(Boolean).map((v) => v.series()), // views can be null (for textboxes)
      force: forceRefresh,
      filters: appliedFilters,
    })
      .then((json) => {
        if (currentRefresh !== latestDashboardRefresh) {
          return;
        }
        dispatch(
          completed(DASHBOARD_REFRESHED, {
            json,
            dashboard: active,
            views,
          }),
        );

        dispatch({
          type: 'DASHBOARD_REFRESHED_LEGACY',
          payload: {
            dashboardId: active._id,
            refreshId: json._refreshId,
            hasAppliedFilters: appliedFilters.length > 0,
          },
        });

        // 2. now reload the widgets to get the latest data (will poll until results are back)
        dispatch(refreshWidgets(false, forceRefresh));
      })
      .catch(standardErrorHandler(DASHBOARD_REFRESHED, dispatch));
  };
}

export function refreshWidgets(
  incrementRetryTime = false,
  showLoaderImmediately = false,
) {
  return (dispatch, getState) => {
    const { viewsAwaitingResults, refreshId, active } = getState().dashboards;
    const dashboardIdToAppliedFiltersRefreshIdMap =
      selectDashboardIdToAppliedFiltersRefreshIdMap(getState());

    if (viewsAwaitingResults.length === 0) {
      return; // nothing to load
    }

    const currentRefresh = refreshId;
    const refreshIdParam =
      dashboardIdToAppliedFiltersRefreshIdMap[active._id] ?? refreshId;

    dispatch(
      loading(DASHBOARD_CONTENT_LOADED, {
        views: viewsAwaitingResults,
        showLoaderImmediately,
      }),
    );
    post(
      'loading dashboard',
      `/dashboards/data${refreshIdParam ? `?refreshId=${refreshIdParam}` : ''}`,
      {
        viewSeries: viewsAwaitingResults.map((v) => v.series()),
      },
    )
      .then((json) => {
        if (getState().dashboards.refreshId !== currentRefresh) {
          return;
        }
        dispatch(
          completed(DASHBOARD_CONTENT_LOADED, { json, incrementRetryTime }),
        );
      })
      .catch(standardErrorHandler(DASHBOARD_CONTENT_LOADED, dispatch))
      .finally(() => dispatch(finished(DASHBOARD_CONTENT_LOADED)));
  };
}

export function refreshPublicPin() {
  return (dispatch, getState) => {
    const { active } = getState().dashboards;

    post('re-generating PIN', `/dashboards/${active.id()}/regenPin`, {
      // empty
    }).then(function (json) {
      dispatch(completed(PIN_REFRESHED, { pin: json.pin }));
      NotificationManager.success('New PIN generated successfully', '', 5000);
    });
  };
}
